import ImportCheckModel             from '@/models/services/map/ImportCheckModel';
import ImportCheckUrlModel          from '@/models/services/map/ImportCheckUrlModel';
import ImportModel                  from '@/models/services/map/ImportModel';
import getTextAndTranslateFromError from '@widesk-core/tools/getTextAndTranslateFromError';
import Alert                        from '@widesk-ui/components/Alert';
import Button                       from '@widesk-ui/components/Button';
import Progress                     from '@widesk-ui/components/Progress';
import Spin                         from '@widesk-ui/components/Spin';
import View                         from '@widesk-ui/components/View';
import { message }                  from '@widesk-ui/hooks/useMessage';
import { observer }                 from 'mobx-react';
import { useEffect }                from 'react';
import { useState }                 from 'react';
import userMercureListener          from '@/services/userMercureListener';
import { OnMessageFunction }        from '@/services/MercureListener';

export type ImportSource = { type: 'file'; file: File } | { type: 'url'; url: string };

interface CheckImportInputProps {
	source: ImportSource;
	projectUrn: Urn;
	importType?: Urn;
	placeType: Urn;
	value?: boolean;
	onChange?: (value?: boolean) => void;
}

const CheckImportInput = observer(({
	source,
	projectUrn,
	importType,
	placeType,
	onChange,
}: CheckImportInputProps) => {
	const [error, setError] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [report, setReport] = useState<ImportModel>();
	const [progress, setProgress] = useState<{ current: number, total: number }>();

	// Changer de fichier ou d'url invalide l'analyse précédente
	const sourceKey = source.type === 'url' ? source.url : source.file;
	useEffect(() => {
		setReport(undefined);
		setError('');
		onChange?.(undefined);
	}, [sourceKey]);

	const check = async () => {
		if (source.type === 'url') {
			const model = new ImportCheckUrlModel();
			await model.patch({
				project: projectUrn,
				importType,
				importInformations: { placeTypeUrn: placeType },
				url: source.url,
			});

			return model;
		}

		const model = new ImportCheckModel();
		await model.patchFormData({
			file: source.file,
			project: projectUrn,
			importType,
			importInformations: JSON.stringify({ placeTypeUrn: placeType }),
		});

		return model;
	};

	return (
		<Spin spinning={isLoading}>
			<View gap={8}>
				<Button
					onClick={() => {
						setIsLoading(true);
						setProgress({ current: 0, total: 0 });

						// L'analyse tourne dans la requête HTTP : Mercure pousse l'avancement ligne par ligne.
						const onProgress: OnMessageFunction = response => {
							setProgress({ current: response.data.current, total: response.data.total });
						};
						userMercureListener.on('validate_import_places_progress', onProgress);

						const done = () => {
							userMercureListener.off('validate_import_places_progress', onProgress);
							setProgress(undefined);
							setIsLoading(false);
						};

						check()
							.then((model) => {
								setReport(model);
								setError('');
								done();
								onChange?.(true);
							})
							.catch((error) => {
								const msg = getTextAndTranslateFromError(error);
								setReport(undefined);
								if (msg) {
									setError(msg);
								} else {
									message.error(error);
								}
								done();
								onChange?.(undefined);
							});
					}}
				>
					{source.type === 'url' ? 'Analyser le fichier distant' : 'Analyser le fichier'}
				</Button>

				{!!progress?.total && (
					<Progress
						type="line"
						percent={Math.floor((progress.current / progress.total) * 100)}
					/>
				)}
				{!!report && !error && (
					<Alert
						description={(
							<View>
								<div>Nouveaux : {report.nbInsertions}</div>
								<div>
									Doublons : {report.nbDuplicates}
									{report.nbDuplicates ? ' (les doublons seront ignorés)' : undefined}
								</div>
							</View>
						)}
						message="Rapport d'importation"
						type="success"
					/>
				)}
				{!!error && (
					<Alert
						description={(
							<View>{error}</View>
						)}
						message="Problème d'importation"
						type="error" />
				)}
			</View>
		</Spin>
	);
});

export default CheckImportInput;
